import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { getInitials, formatCurrency } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import PayrollEmployees from './PayrollEmployees';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  Users, Briefcase, FileText, ChevronRight, Plus, Check, 
  Clock, ArrowRight, Building, UserCheck, TrendingUp, Calendar, Eye, EyeOff,
  Save, X, Pencil, AlertCircle, CheckCircle2, ChevronDown, ChevronUp,
  Download, FileDown, ExternalLink, Printer, Landmark, IndianRupee, Banknote, Trash2,
  Mail, Loader2, Send, Settings
} from 'lucide-react';

function getFYOptions() {
  const now = new Date();
  const currentFYStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const options = [];
  for (let y = currentFYStart; y >= currentFYStart - 2; y--) {
    options.push({ value: `${y}-${String(y + 1).slice(2)}`, label: `FY ${y}-${String(y + 1).slice(2)}` });
  }
  return options;
}


const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-time', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'part_time', label: 'Part-time', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  { value: 'contractor', label: 'Contractor', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  { value: 'freelancer', label: 'Freelancer', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' }
];

const PAY_TYPES = [
  { value: 'salaried', label: 'Salaried' },
  { value: 'hourly', label: 'Hourly' }
];

const PAY_SCHEDULES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'semimonthly', label: 'Semi-monthly' },
  { value: 'monthly', label: 'Monthly' }
];

export default function Payroll() {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [payRuns, setPayRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [genPeriodStart, setGenPeriodStart] = useState('');
  const [genPeriodEnd, setGenPeriodEnd] = useState('');
  const [exportFormats, setExportFormats] = useState([]);
  const [showExportMenu, setShowExportMenu] = useState(null);
  const exportMenuRef = React.useRef(null);

  // Close export menu on click outside
  React.useEffect(() => {
    if (!showExportMenu) return;
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExportMenu]);
  const [generateError, setGenerateError] = useState('');
  const [bankEditEmployee, setBankEditEmployee] = useState(null);
  const [bankForm, setBankForm] = useState({});
  const [salaryTemplates, setSalaryTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', components: [] });
  const [templatePreview, setTemplatePreview] = useState(null);
  const [previewGross, setPreviewGross] = useState(50000);
  const [expandedPayslip, setExpandedPayslip] = useState(null);
  const [sendingPayslips, setSendingPayslips] = useState(false);
  const [payrollSettings, setPayrollSettings] = useState(null);
  const [statesList, setStatesList] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  const [editPaySlip, setEditPaySlip] = useState(null); // payslip being edited
  const [editPayItems, setEditPayItems] = useState([]); // [{name, category, amount}]
  const [editPayTdsOverride, setEditPayTdsOverride] = useState(null);
  const [editPaySaving, setEditPaySaving] = useState(false);

  const [accessDenied, setAccessDenied] = useState(false);

  // FY Overview state
  const fyOptions = getFYOptions();
  const [overviewFY, setOverviewFY] = useState(fyOptions[0]?.value || '');
  const [fyOverview, setFyOverview] = useState(null);
  const [fyLoading, setFyLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sumRes, empRes, runsRes, fmtRes, tmplRes, settingsRes, statesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/payroll/summary`, { headers }),
        fetch(`${API_URL}/api/admin/payroll/employees`, { headers }),
        fetch(`${API_URL}/api/admin/payroll/runs`, { headers }),
        fetch(`${API_URL}/api/admin/payroll/export-formats`, { headers }),
        fetch(`${API_URL}/api/admin/payroll/salary-templates`, { headers }),
        fetch(`${API_URL}/api/admin/payroll/settings`, { headers }),
        fetch(`${API_URL}/api/admin/payroll/states`, { headers })
      ]);
      
      // Check for 403 access denied
      if (sumRes.status === 403 || empRes.status === 403 || runsRes.status === 403) {
        setAccessDenied(true);
        return;
      }
      
      if (sumRes.ok) setSummary(await sumRes.json());
      if (empRes.ok) setEmployees(await empRes.json());
      if (runsRes.ok) setPayRuns(await runsRes.json());
      if (fmtRes.ok) setExportFormats(await fmtRes.json());
      if (tmplRes.ok) setSalaryTemplates(await tmplRes.json());
      if (settingsRes.ok) setPayrollSettings(await settingsRes.json());
      if (statesRes.ok) setStatesList(await statesRes.json());
    } catch (err) {
      console.error('Payroll fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch FY overview when tab is overview or FY changes
  const fetchFYOverview = useCallback(async () => {
    if (!token || !overviewFY) return;
    setFyLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/fy-overview?fy=${overviewFY}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setFyOverview(await res.json());
    } catch (err) {
      console.error('FY overview error:', err);
    } finally {
      setFyLoading(false);
    }
  }, [token, overviewFY]);

  useEffect(() => {
    if (activeTab === 'overview') fetchFYOverview();
  }, [activeTab, fetchFYOverview]);

  const handleEditEmployee = (emp) => {
    setEditingEmployee(emp.id);
    setEditForm({
      employment_type: emp.employment_type,
      pay_type: emp.pay_type,
      pay_rate: emp.pay_rate,
      pay_currency: emp.pay_currency,
      pay_schedule: emp.pay_schedule,
      weekly_hours_target: emp.weekly_hours_target,
      overtime_eligible: emp.overtime_eligible,
      overtime_rate_multiplier: emp.overtime_rate_multiplier,
      date_of_joining: emp.date_of_joining || '',
      employee_id_number: emp.employee_id_number || ''
    });
  };

  const handleSaveEmployee = async () => {
    try {
      await fetch(`${API_URL}/api/admin/payroll/employees/${editingEmployee}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editForm)
      });
      setEditingEmployee(null);
      fetchData();
    } catch (err) {
      console.error('Save employee error:', err);
    }
  };


  const handleOpenEditPay = (ps) => {
    const existingAdj = (ps.components || []).filter(c => c.is_adjustment);
    setEditPaySlip(ps);
    setEditPayItems(existingAdj.map(c => ({ name: c.name, category: c.category, amount: c.amount })));
    const currentTds = (ps.components || []).find(c => c.code === 'tds');
    setEditPayTdsOverride(null); // null = keep auto
    setEditPaySaving(false);
  };

  const handleSaveEditPay = async () => {
    if (!selectedRun || !editPaySlip) return;
    setEditPaySaving(true);
    try {
      const body = { items: editPayItems.filter(i => i.name && i.amount) };
      if (editPayTdsOverride !== null) body.tds_override = editPayTdsOverride;
      const res = await fetch(`${API_URL}/api/admin/payroll/runs/${selectedRun.id}/payslips/${editPaySlip.id}/adjustments`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const updated = await res.json();
        setSelectedRun(updated);
        setPayRuns(prev => prev.map(r => r.id === updated.id ? updated : r));
        setEditPaySlip(null);
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to save');
      }
    } catch (err) { console.error('Save edit pay error:', err); } finally { setEditPaySaving(false); }
  };

  const handleOpenBankEdit = (emp) => {
    setBankEditEmployee(emp.id);
    setBankForm({
      bank_name: emp.bank_name || '',
      bank_account_number: emp.bank_account_number || '',
      bank_ifsc_code: emp.bank_ifsc_code || '',
      bank_branch: emp.bank_branch || '',
      pan_number: emp.pan_number || '',
      uan_number: emp.uan_number || ''
    });
  };

  const handleSaveBankDetails = async () => {
    try {
      await fetch(`${API_URL}/api/admin/payroll/employees/${bankEditEmployee}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(bankForm)
      });
      setBankEditEmployee(null);
      fetchData();
    } catch (err) {
      console.error('Save bank details error:', err);
    }
  };

  const handleSeedIndiaTemplate = async () => {
    try {
      await fetch(`${API_URL}/api/admin/payroll/salary-templates/seed-india`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Seed India template error:', err);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      // Assign sort_order to components
      const components = templateForm.components.map((c, i) => ({ ...c, sort_order: i + 1 }));
      const payload = { name: templateForm.name, components, is_default: templateForm.is_default };
      
      const url = editingTemplate === 'new'
        ? `${API_URL}/api/admin/payroll/salary-templates`
        : `${API_URL}/api/admin/payroll/salary-templates/${editingTemplate}`;
      const res = await fetch(url, {
        method: editingTemplate === 'new' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setEditingTemplate(null);
        setTemplatePreview(null);
        fetchData();
      }
    } catch (err) {
      console.error('Save template error:', err);
    }
  };

  const handleEditTemplate = (tmpl) => {
    setEditingTemplate(tmpl.id);
    setTemplateForm({
      name: tmpl.name,
      is_default: tmpl.is_default,
      components: tmpl.components.map(c => ({ ...c }))
    });
    setTemplatePreview(null);
  };

  const handleCreateTemplate = () => {
    setEditingTemplate('new');
    setTemplateForm({
      name: '',
      is_default: false,
      components: [
        { name: 'Basic', code: 'basic', type: 'percentage', value: 40, category: 'earning', base_on: 'gross' },
        { name: 'Special Allowance', code: 'special', type: 'remainder', value: 0, category: 'earning', base_on: 'gross' }
      ]
    });
    setTemplatePreview(null);
  };

  const handleAddComponent = (category) => {
    const newComp = {
      name: '', code: '', type: category === 'earning' ? 'percentage' : 'fixed',
      value: 0, category, base_on: category === 'earning' ? 'gross' : 'basic'
    };
    setTemplateForm(prev => ({ ...prev, components: [...prev.components, newComp] }));
  };

  const handleUpdateComponent = (idx, field, value) => {
    setTemplateForm(prev => {
      const comps = [...prev.components];
      comps[idx] = { ...comps[idx], [field]: value };
      // Auto-generate code from name
      if (field === 'name') {
        comps[idx].code = value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      }
      return { ...prev, components: comps };
    });
  };

  const handleRemoveComponent = (idx) => {
    setTemplateForm(prev => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== idx)
    }));
  };

  const handleSetDefaultTemplate = async (templateId) => {
    try {
      await fetch(`${API_URL}/api/admin/payroll/salary-templates/${templateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_default: true })
      });
      fetchData();
    } catch (err) {
      console.error('Set default template error:', err);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await fetch(`${API_URL}/api/admin/payroll/salary-templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Delete template error:', err);
    }
  };

  const handlePreviewTemplate = async (templateId, gross) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/salary-templates/${templateId}/preview?gross=${gross}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setTemplatePreview(await res.json());
    } catch (err) {
      console.error('Preview template error:', err);
    }
  };

  const handleAssignTemplate = async (empId, templateId) => {
    try {
      await fetch(`${API_URL}/api/admin/payroll/employees/${empId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ salary_template_id: templateId || null })
      });
      fetchData();
    } catch (err) {
      console.error('Assign template error:', err);
    }
  };

  const handleRecalculateRun = async (runId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/runs/${runId}/recalculate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const updated = await res.json();
        setSelectedRun(updated);
        fetchData();
      }
    } catch (err) {
      console.error('Recalculate error:', err);
    }
  };

  const handleGeneratePayRun = async () => {
    if (!genPeriodStart || !genPeriodEnd) return;
    setGenerating(true);
    setGenerateError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ period_start: genPeriodStart, period_end: genPeriodEnd })
      });
      if (res.ok) {
        setShowGenerateForm(false);
        setGenPeriodStart('');
        setGenPeriodEnd('');
        fetchData();
      } else {
        const err = await res.json();
        setGenerateError(err.detail || 'Failed to generate pay run');
      }
    } catch (err) {
      console.error('Generate pay run error:', err);
      setGenerateError('Network error. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSavePayrollSettings = async (updatedSettings) => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedSettings)
      });
      if (res.ok) {
        const data = await res.json();
        setPayrollSettings(prev => ({ ...prev, ...updatedSettings }));
        alert('Payroll settings saved successfully!');
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Save settings error:', err);
      alert('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };


  const handleUpdateRunStatus = async (runId, status) => {
    try {
      await fetch(`${API_URL}/api/admin/payroll/runs/${runId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      fetchData();
      if (selectedRun?.id === runId) {
        const res = await fetch(`${API_URL}/api/admin/payroll/runs/${runId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedRun(await res.json());
      }
    } catch (err) {
      console.error('Update run status error:', err);
    }
  };

  const handleExportCSV = async (runId, formatKey) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/runs/${runId}/export/${formatKey}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') || `payroll_${formatKey}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setShowExportMenu(null);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const CATEGORY_LABELS = { payment: 'Payment Exports', payroll_system: 'Payroll Systems', accounting: 'Accounting' };
  const CATEGORY_ORDER = ['payment', 'payroll_system', 'accounting'];

  const renderGroupedExportMenu = (runId, filterFn) => {
    const formats = filterFn ? exportFormats.filter(filterFn) : exportFormats;
    const grouped = CATEGORY_ORDER.map(cat => ({
      cat, label: CATEGORY_LABELS[cat], items: formats.filter(f => f.category === cat)
    })).filter(g => g.items.length > 0);

    return (
      <div ref={exportMenuRef} className="absolute right-0 top-8 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-50 max-h-[420px] overflow-y-auto" data-testid="export-menu">
        {grouped.map((group, gi) => (
          <div key={group.cat}>
            {gi > 0 && <div className="mx-2 my-1 border-t border-slate-100 dark:border-slate-700" />}
            <div className="px-3 pt-2 pb-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{group.label}</span>
            </div>
            {group.items.map(fmt => (
              <button key={fmt.key} onClick={() => handleExportCSV(runId, fmt.key)}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2 transition-colors"
                data-testid={`export-${fmt.key}`}>
                <FileDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-slate-700 dark:text-slate-200 truncate">{fmt.name}</p>
                  <p className="text-[10px] text-slate-400 truncate">{fmt.description}</p>
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const handleDownloadPayslipPDF = async (runId, userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/runs/${runId}/payslip-pdf/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip_${runId}_${userId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
    }
  };

  const handleViewRun = async (runId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/runs/${runId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedRun(await res.json());
      setActiveTab('run-detail');
    } catch (err) {
      console.error('View run error:', err);
    }
  };

  const handleSendPayslips = async (runId) => {
    if (!window.confirm('This will email payslip PDFs to all active employees in this pay run. Continue?')) return;
    setSendingPayslips(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/payroll/runs/${runId}/send-payslips`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        alert(`${data.message}`);
        // Refresh the run to show sent timestamp
        if (selectedRun?.id === runId) {
          const runRes = await fetch(`${API_URL}/api/admin/payroll/runs/${runId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSelectedRun(await runRes.json());
        }
        fetchData();
      } else {
        alert(data.detail || 'Failed to send payslips');
      }
    } catch (err) {
      console.error('Send payslips error:', err);
      alert('Failed to send payslips. Please try again.');
    } finally {
      setSendingPayslips(false);
    }
  };

  const getTypeStyle = (type) => EMPLOYMENT_TYPES.find(t => t.value === type) || EMPLOYMENT_TYPES[0];

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div></Layout>;

  if (accessDenied) return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 py-6" data-testid="payroll-access-denied">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Access Denied</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">You don't have permission to access the Payroll module. This feature is restricted to administrators.</p>
        </div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 py-6" data-testid="payroll-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Payroll</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage compensation, generate pay runs, and track payroll costs</p>
          </div>
          {activeTab === 'runs' && (
            <Button
              onClick={() => setShowGenerateForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              data-testid="generate-payrun-btn"
            >
              <Plus className="w-4 h-4" /> Generate Pay Run
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg w-fit mb-6">
          {[
            { value: 'overview', label: 'Overview', icon: TrendingUp },
            { value: 'employees', label: 'Employees', icon: Users },
            { value: 'templates', label: 'Salary Structure', icon: Landmark },
            { value: 'runs', label: 'Pay Runs', icon: FileText },
            { value: 'settings', label: 'Settings', icon: Settings }
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => { setActiveTab(tab.value); setSelectedRun(null); }}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                activeTab === tab.value
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`payroll-tab-${tab.value}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Generate Pay Run Modal */}
        {showGenerateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-[440px] shadow-xl" data-testid="generate-payrun-modal">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Generate Pay Run</h3>
              {generateError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2" data-testid="generate-error">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-400">{generateError}</p>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Period Start</label>
                  <input type="date" value={genPeriodStart} onChange={e => setGenPeriodStart(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Period End</label>
                  <input type="date" value={genPeriodEnd} onChange={e => setGenPeriodEnd(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => { setShowGenerateForm(false); setGenerateError(''); }}>Cancel</Button>
                <Button onClick={handleGeneratePayRun} disabled={generating || !genPeriodStart || !genPeriodEnd}
                  className="bg-blue-600 hover:bg-blue-700 text-white">
                  {generating ? 'Generating...' : 'Generate'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-6" data-testid="payroll-overview">
            {/* FY Selector */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Financial Year Overview</h2>
              <Select value={overviewFY} onValueChange={setOverviewFY}>
                <SelectTrigger className="w-[160px]" data-testid="overview-fy-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fyOptions.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {fyLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* YTD Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { label: 'Total Payroll Cost', value: fyOverview?.totals?.gross || 0, icon: Banknote, color: 'bg-emerald-50 dark:bg-emerald-900/30', iconColor: 'text-emerald-600' },
                    { label: 'Total Net Pay', value: fyOverview?.totals?.net || 0, icon: IndianRupee, color: 'bg-blue-50 dark:bg-blue-900/30', iconColor: 'text-blue-600' },
                    { label: 'Total TDS', value: fyOverview?.totals?.tds || 0, icon: FileText, color: 'bg-red-50 dark:bg-red-900/30', iconColor: 'text-red-600' },
                    { label: 'Total PF', value: fyOverview?.totals?.pf || 0, icon: Building, color: 'bg-purple-50 dark:bg-purple-900/30', iconColor: 'text-purple-600' },
                    { label: 'Total PT', value: fyOverview?.totals?.pt || 0, icon: Landmark, color: 'bg-amber-50 dark:bg-amber-900/30', iconColor: 'text-amber-600' },
                    { label: 'Avg Headcount', value: fyOverview?.totals?.headcount || 0, icon: Users, color: 'bg-slate-100 dark:bg-slate-800', iconColor: 'text-slate-600', isCurrency: false },
                  ].map((card, i) => (
                    <Card key={i} className="border-slate-200/60 dark:border-slate-700">
                      <CardContent className="p-4">
                        <div className={`w-8 h-8 rounded-lg ${card.color} flex items-center justify-center mb-2`}>
                          <card.icon className={`w-4 h-4 ${card.iconColor}`} />
                        </div>
                        <p className="text-lg font-bold text-slate-900 dark:text-white" data-testid={`overview-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
                          {card.isCurrency === false ? card.value : formatCurrency(card.value, fyOverview?.currency || 'INR')}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wider font-medium">{card.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Monthly Bar Chart */}
                {fyOverview?.months?.some(m => m.runs > 0) && (
                  <Card className="border-slate-200/60 dark:border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-slate-400" />
                        Monthly Payroll Trend — FY {overviewFY}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={fyOverview.months} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                          <Tooltip
                            formatter={(value, name) => [formatCurrency(value, fyOverview?.currency || 'INR'), name]}
                            labelStyle={{ fontWeight: 600 }}
                            contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                          />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Bar dataKey="gross" name="Gross Pay" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="deductions" name="Deductions" fill="#ef4444" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="net" name="Net Pay" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Monthly Breakdown Table */}
                <Card className="border-slate-200/60 dark:border-slate-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      Monthly Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="fy-monthly-table">
                        <thead>
                          <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                            <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-slate-500">Month</th>
                            <th className="text-center px-3 py-3 font-semibold text-xs uppercase tracking-wider text-slate-500">Headcount</th>
                            <th className="text-right px-3 py-3 font-semibold text-xs uppercase tracking-wider text-slate-500">Gross Pay</th>
                            <th className="text-right px-3 py-3 font-semibold text-xs uppercase tracking-wider text-slate-500">PF</th>
                            <th className="text-right px-3 py-3 font-semibold text-xs uppercase tracking-wider text-slate-500">PT</th>
                            <th className="text-right px-3 py-3 font-semibold text-xs uppercase tracking-wider text-slate-500">TDS</th>
                            <th className="text-right px-3 py-3 font-semibold text-xs uppercase tracking-wider text-slate-500">Total Ded.</th>
                            <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider text-slate-500">Net Pay</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(fyOverview?.months || []).map((m, idx) => (
                            <tr key={idx} className={`border-b border-slate-100 dark:border-slate-700/50 ${m.runs > 0 ? '' : 'opacity-40'}`}>
                              <td className="px-4 py-2.5 font-medium text-slate-900 dark:text-white">{m.label}</td>
                              <td className="text-center px-3 py-2.5 text-slate-600 dark:text-slate-300">{m.runs > 0 ? m.headcount : '—'}</td>
                              <td className="text-right px-3 py-2.5 tabular-nums text-slate-700 dark:text-slate-300">{m.runs > 0 ? formatCurrency(m.gross, fyOverview?.currency || 'INR') : '—'}</td>
                              <td className="text-right px-3 py-2.5 tabular-nums text-slate-500">{m.runs > 0 ? formatCurrency(m.pf, fyOverview?.currency || 'INR') : '—'}</td>
                              <td className="text-right px-3 py-2.5 tabular-nums text-slate-500">{m.runs > 0 ? formatCurrency(m.pt, fyOverview?.currency || 'INR') : '—'}</td>
                              <td className="text-right px-3 py-2.5 tabular-nums text-red-600 dark:text-red-400">{m.runs > 0 ? formatCurrency(m.tds, fyOverview?.currency || 'INR') : '—'}</td>
                              <td className="text-right px-3 py-2.5 tabular-nums text-slate-500">{m.runs > 0 ? formatCurrency(m.deductions, fyOverview?.currency || 'INR') : '—'}</td>
                              <td className="text-right px-4 py-2.5 tabular-nums font-semibold text-slate-900 dark:text-white">{m.runs > 0 ? formatCurrency(m.net, fyOverview?.currency || 'INR') : '—'}</td>
                            </tr>
                          ))}
                          {/* Totals Row */}
                          {fyOverview?.totals?.runs > 0 && (
                            <tr className="border-t-2 border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 font-bold">
                              <td className="px-4 py-3 text-slate-900 dark:text-white">YTD Total</td>
                              <td className="text-center px-3 py-3 text-slate-700 dark:text-slate-300">{fyOverview.totals.headcount}</td>
                              <td className="text-right px-3 py-3 tabular-nums text-slate-900 dark:text-white">{formatCurrency(fyOverview.totals.gross, fyOverview?.currency || 'INR')}</td>
                              <td className="text-right px-3 py-3 tabular-nums text-slate-700">{formatCurrency(fyOverview.totals.pf, fyOverview?.currency || 'INR')}</td>
                              <td className="text-right px-3 py-3 tabular-nums text-slate-700">{formatCurrency(fyOverview.totals.pt, fyOverview?.currency || 'INR')}</td>
                              <td className="text-right px-3 py-3 tabular-nums text-red-700">{formatCurrency(fyOverview.totals.tds, fyOverview?.currency || 'INR')}</td>
                              <td className="text-right px-3 py-3 tabular-nums text-slate-700">{formatCurrency(fyOverview.totals.deductions, fyOverview?.currency || 'INR')}</td>
                              <td className="text-right px-4 py-3 tabular-nums text-slate-900 dark:text-white">{formatCurrency(fyOverview.totals.net, fyOverview?.currency || 'INR')}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {fyOverview?.totals?.runs === 0 && (
                      <div className="text-center py-10">
                        <FileText className="w-8 h-8 mx-auto text-slate-200 dark:text-slate-600 mb-2" />
                        <p className="text-sm text-slate-400">No processed pay runs for FY {overviewFY}</p>
                        <p className="text-xs text-slate-400 mt-1">Generate and process payroll to see data here</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Pay Runs (existing) */}
                {summary && (
                  <Card className="border-slate-200/60 dark:border-slate-700">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        Recent Pay Runs
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {summary.recent_runs.length > 0 ? (
                        <div className="space-y-2">
                          {summary.recent_runs.map(run => (
                            <div key={run.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer"
                              onClick={() => handleViewRun(run.id)}>
                              <div className="flex items-center gap-3">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <div>
                                  <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                    {new Date(run.period_start).toLocaleDateString()} — {new Date(run.period_end).toLocaleDateString()}
                                  </p>
                                  <p className="text-xs text-slate-400">{run.employee_count} employees</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                                  run.status === 'processed' ? 'bg-emerald-100 text-emerald-700' :
                                  run.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>{run.status}</span>
                                <span className="text-sm font-bold text-slate-900 dark:text-white">{formatCurrency(run.total_amount, run.currency)}</span>
                                <ChevronRight className="w-4 h-4 text-slate-300" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <FileText className="w-8 h-8 mx-auto text-slate-200 dark:text-slate-600 mb-2" />
                          <p className="text-sm text-slate-400">No pay runs yet. Generate your first pay run to get started.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}

        {/* EMPLOYEES TAB */}
        {activeTab === 'employees' && (
          <PayrollEmployees employees={employees} token={token} onDownloadPayslip={handleDownloadPayslipPDF} onEmployeeUpdated={fetchData} salaryTemplates={salaryTemplates} />
        )}






        {/* Bank Details Modal */}
        {bankEditEmployee && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-[500px] shadow-xl" data-testid="bank-details-modal">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Bank & Tax Details</h3>
                  <p className="text-xs text-slate-400">{employees.find(e => e.id === bankEditEmployee)?.name}</p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Bank Information</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Bank Name</label>
                    <input type="text" value={bankForm.bank_name} onChange={e => setBankForm({...bankForm, bank_name: e.target.value})}
                      placeholder="e.g. HDFC Bank"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      data-testid="bank-name-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Branch</label>
                    <input type="text" value={bankForm.bank_branch} onChange={e => setBankForm({...bankForm, bank_branch: e.target.value})}
                      placeholder="e.g. Koramangala"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      data-testid="bank-branch-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Account Number</label>
                    <input type="text" value={bankForm.bank_account_number} onChange={e => setBankForm({...bankForm, bank_account_number: e.target.value})}
                      placeholder="e.g. 1234567890123"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      data-testid="bank-account-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">IFSC Code</label>
                    <input type="text" value={bankForm.bank_ifsc_code} onChange={e => setBankForm({...bankForm, bank_ifsc_code: e.target.value.toUpperCase()})}
                      placeholder="e.g. HDFC0001234"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      data-testid="bank-ifsc-input" />
                  </div>
                </div>

                <div className="border-t border-slate-100 dark:border-slate-700 pt-4 mt-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Tax & Statutory</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">PAN Number</label>
                      <input type="text" value={bankForm.pan_number} onChange={e => setBankForm({...bankForm, pan_number: e.target.value.toUpperCase()})}
                        placeholder="e.g. ABCDE1234F"
                        maxLength={10}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                        data-testid="pan-input" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">UAN (PF Account)</label>
                      <input type="text" value={bankForm.uan_number} onChange={e => setBankForm({...bankForm, uan_number: e.target.value})}
                        placeholder="e.g. 100012345678"
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                        data-testid="uan-input" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setBankEditEmployee(null)}>Cancel</Button>
                <Button onClick={handleSaveBankDetails} className="bg-blue-600 hover:bg-blue-700 text-white gap-1" data-testid="save-bank-btn">
                  <Save className="w-3.5 h-3.5" /> Save Details
                </Button>
              </div>
            </div>
          </div>
        )}


        {/* SALARY STRUCTURE TAB */}
        {activeTab === 'templates' && (
          <div className="space-y-5" data-testid="salary-templates">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-white">Salary Templates</h2>
                <p className="text-xs text-slate-400 mt-0.5">Define how CTC/gross is split into earnings and deductions</p>
              </div>
              <div className="flex gap-2">
                {!salaryTemplates.some(t => t.region === 'india' && t.is_preset) && (
                  <Button variant="outline" onClick={handleSeedIndiaTemplate} className="text-xs gap-1" data-testid="seed-india-btn">
                    <IndianRupee className="w-3.5 h-3.5" /> Add India Presets
                  </Button>
                )}
                <Button onClick={handleCreateTemplate} className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1" data-testid="create-template-btn">
                  <Plus className="w-3.5 h-3.5" /> New Template
                </Button>
              </div>
            </div>

            {/* Template Editor Modal */}
            {editingTemplate && (
              <Card className="border-blue-200 dark:border-blue-800 ring-2 ring-blue-500/20" data-testid="template-editor">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                      {editingTemplate === 'new' ? 'Create New Template' : 'Edit Template'}
                    </h3>
                    <button onClick={() => setEditingTemplate(null)} className="text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Template Name</label>
                    <input type="text" value={templateForm.name} onChange={e => setTemplateForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="e.g. India - Custom CTC"
                      className="w-full max-w-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                      data-testid="template-name-input" />
                  </div>

                  {/* Earnings */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Earnings</p>
                      <Button size="sm" variant="ghost" onClick={() => handleAddComponent('earning')} className="text-xs h-6 px-2 gap-1 text-emerald-600" data-testid="add-earning-btn">
                        <Plus className="w-3 h-3" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {templateForm.components.filter(c => c.category === 'earning').map((comp, rawIdx) => {
                        const idx = templateForm.components.indexOf(comp);
                        return (
                          <div key={idx} className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30" data-testid={`earning-${idx}`}>
                            <input type="text" value={comp.name} onChange={e => handleUpdateComponent(idx, 'name', e.target.value)}
                              placeholder="Component name" className="flex-1 px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm min-w-0" />
                            <select value={comp.type} onChange={e => handleUpdateComponent(idx, 'type', e.target.value)}
                              className="px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs w-28">
                              <option value="percentage">% of</option>
                              <option value="fixed">Fixed ₹</option>
                              <option value="remainder">Remainder</option>
                            </select>
                            {comp.type === 'percentage' && (
                              <>
                                <input type="number" value={comp.value} onChange={e => handleUpdateComponent(idx, 'value', parseFloat(e.target.value) || 0)}
                                  className="w-16 px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-right" />
                                <span className="text-xs text-slate-400">%</span>
                                <select value={comp.base_on || 'gross'} onChange={e => handleUpdateComponent(idx, 'base_on', e.target.value)}
                                  className="px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs w-20">
                                  <option value="gross">Gross</option>
                                  <option value="basic">Basic</option>
                                </select>
                              </>
                            )}
                            {comp.type === 'fixed' && (
                              <input type="number" value={comp.value} onChange={e => handleUpdateComponent(idx, 'value', parseFloat(e.target.value) || 0)}
                                className="w-24 px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-right" placeholder="Amount" />
                            )}
                            {comp.type === 'remainder' && (
                              <span className="text-xs text-slate-400 italic">Auto-calculated</span>
                            )}
                            <button onClick={() => handleRemoveComponent(idx)} className="p-1 text-red-400 hover:text-red-600" data-testid={`remove-earning-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Deductions */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold text-red-500 uppercase tracking-wider">Deductions</p>
                      <Button size="sm" variant="ghost" onClick={() => handleAddComponent('deduction')} className="text-xs h-6 px-2 gap-1 text-red-500" data-testid="add-deduction-btn">
                        <Plus className="w-3 h-3" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {templateForm.components.filter(c => c.category === 'deduction').map((comp) => {
                        const idx = templateForm.components.indexOf(comp);
                        return (
                          <div key={idx} className="flex items-center gap-2 p-2.5 rounded-lg bg-red-50/50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30" data-testid={`deduction-${idx}`}>
                            <input type="text" value={comp.name} onChange={e => handleUpdateComponent(idx, 'name', e.target.value)}
                              placeholder="Deduction name" className="flex-1 px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm min-w-0" />
                            <select value={comp.type} onChange={e => handleUpdateComponent(idx, 'type', e.target.value)}
                              className="px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs w-28">
                              <option value="percentage">% of</option>
                              <option value="fixed">Fixed ₹</option>
                            </select>
                            {comp.type === 'percentage' && (
                              <>
                                <input type="number" value={comp.value} onChange={e => handleUpdateComponent(idx, 'value', parseFloat(e.target.value) || 0)}
                                  className="w-16 px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-right" />
                                <span className="text-xs text-slate-400">%</span>
                                <select value={comp.base_on || 'basic'} onChange={e => handleUpdateComponent(idx, 'base_on', e.target.value)}
                                  className="px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs w-20">
                                  <option value="gross">Gross</option>
                                  <option value="basic">Basic</option>
                                </select>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-slate-400">Cap:</span>
                                  <input type="number" value={comp.cap_base || ''} onChange={e => handleUpdateComponent(idx, 'cap_base', parseFloat(e.target.value) || null)}
                                    placeholder="None" className="w-20 px-1.5 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-xs text-right" />
                                </div>
                              </>
                            )}
                            {comp.type === 'fixed' && (
                              <input type="number" value={comp.value} onChange={e => handleUpdateComponent(idx, 'value', parseFloat(e.target.value) || 0)}
                                className="w-24 px-2 py-1.5 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-right" placeholder="Amount" />
                            )}
                            <button onClick={() => handleRemoveComponent(idx)} className="p-1 text-red-400 hover:text-red-600" data-testid={`remove-deduction-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })}
                      {templateForm.components.filter(c => c.category === 'deduction').length === 0 && (
                        <p className="text-xs text-slate-400 italic py-2 px-3">No deductions — click Add to include PF, PT, ESI, etc.</p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                    <Button variant="outline" onClick={() => setEditingTemplate(null)}>Cancel</Button>
                    <Button onClick={handleSaveTemplate} disabled={!templateForm.name || templateForm.components.length === 0}
                      className="bg-blue-600 hover:bg-blue-700 text-white gap-1" data-testid="save-template-btn">
                      <Save className="w-3.5 h-3.5" /> {editingTemplate === 'new' ? 'Create Template' : 'Save Changes'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Template Cards */}
            {salaryTemplates.length > 0 ? (
              <div className="space-y-4">
                {salaryTemplates.map(tmpl => (
                  <Card key={tmpl.id} className={`border-slate-200/60 dark:border-slate-700 ${tmpl.is_default ? 'ring-2 ring-blue-500/30' : ''}`} data-testid={`template-${tmpl.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tmpl.region === 'india' ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
                            {tmpl.region === 'india' ? <IndianRupee className="w-4 h-4 text-orange-600" /> : <Banknote className="w-4 h-4 text-blue-600" />}
                          </div>
                          <div>
                            <h3 className="text-sm font-bold text-slate-900 dark:text-white">{tmpl.name}</h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 font-medium uppercase">{tmpl.region}</span>
                              {tmpl.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">DEFAULT</span>}
                              {tmpl.is_preset && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 font-medium">PRESET</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleEditTemplate(tmpl)} className="text-xs h-7 px-2 gap-1" data-testid={`edit-template-${tmpl.id}`}>
                            <Pencil className="w-3 h-3" /> Edit
                          </Button>
                          {!tmpl.is_default && (
                            <Button size="sm" variant="ghost" onClick={() => handleSetDefaultTemplate(tmpl.id)} className="text-xs h-7 px-2" data-testid={`set-default-${tmpl.id}`}>
                              Set Default
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(tmpl.id)} className="text-xs h-7 px-2 text-red-500 hover:text-red-700" data-testid={`delete-template-${tmpl.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Components display */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-2">Earnings</p>
                          <div className="space-y-1.5">
                            {tmpl.components.filter(c => c.category === 'earning').map((c, i) => (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <span className="text-slate-600 dark:text-slate-400">{c.name}</span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">
                                  {c.type === 'percentage' ? `${c.value}% of ${c.base_on === 'basic' ? 'Basic' : 'Gross'}` :
                                   c.type === 'fixed' ? formatCurrency(c.value) : 'Remainder'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-red-500 uppercase tracking-wider mb-2">Deductions</p>
                          <div className="space-y-1.5">
                            {tmpl.components.filter(c => c.category === 'deduction').map((c, i) => (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <span className="text-slate-600 dark:text-slate-400">{c.name}</span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">
                                  {c.type === 'percentage' ? `${c.value}% of ${c.base_on === 'basic' ? 'Basic' : 'Gross'}${c.cap_base ? ` (cap ₹${c.cap_base.toLocaleString()})` : ''}` :
                                   c.type === 'fixed' ? formatCurrency(c.value) : '—'}
                                </span>
                              </div>
                            ))}
                            {tmpl.components.filter(c => c.category === 'deduction').length === 0 && (
                              <p className="text-xs text-slate-400 italic">No deductions</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Preview */}
                      <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Preview Breakdown</span>
                          <input type="number" value={previewGross} onChange={e => setPreviewGross(parseInt(e.target.value) || 0)}
                            className="w-28 text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-right"
                            data-testid="preview-gross-input" />
                          <Button size="sm" variant="outline" onClick={() => handlePreviewTemplate(tmpl.id, previewGross)} className="text-xs h-6 px-2" data-testid={`preview-btn-${tmpl.id}`}>
                            Calculate
                          </Button>
                        </div>
                        {templatePreview && templatePreview.template_name === tmpl.name && (
                          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 space-y-1.5" data-testid="template-preview">
                            {templatePreview.components.map((c, i) => (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <span className={`${c.category === 'deduction' ? 'text-red-500' : 'text-slate-600 dark:text-slate-400'}`}>
                                  {c.category === 'deduction' ? '− ' : '+ '}{c.name}
                                </span>
                                <span className={`font-mono font-semibold ${c.category === 'deduction' ? 'text-red-600' : 'text-slate-800 dark:text-slate-200'}`}>
                                  {formatCurrency(c.amount)}
                                </span>
                              </div>
                            ))}
                            <div className="border-t border-slate-200 dark:border-slate-600 pt-1.5 mt-2 flex items-center justify-between text-xs font-bold">
                              <span className="text-slate-900 dark:text-white">Net Pay</span>
                              <span className="text-emerald-600 font-mono">{formatCurrency(templatePreview.netPay)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-slate-200/60 dark:border-slate-700">
                <CardContent className="text-center py-12">
                  <Landmark className="w-10 h-10 mx-auto text-slate-200 dark:text-slate-600 mb-3" />
                  <p className="text-sm font-semibold text-slate-500">No salary templates configured</p>
                  <p className="text-xs text-slate-400 mt-1 mb-4">Add the India preset to get started with standard CTC components</p>
                  <Button onClick={handleSeedIndiaTemplate} className="bg-blue-600 hover:bg-blue-700 text-white gap-1" data-testid="seed-india-empty-btn">
                    <IndianRupee className="w-4 h-4" /> Add India Presets
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Template Assignment */}
            {salaryTemplates.length > 0 && employees.length > 0 && (
              <Card className="border-slate-200/60 dark:border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    Template Assignments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {employees.map(emp => {
                      const def = salaryTemplates.find(t => t.is_default);
                      return (
                        <div key={emp.id} className="flex items-center justify-between py-2 px-3 rounded-lg border border-slate-100 dark:border-slate-700" data-testid={`assign-${emp.id}`}>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-7 w-7">
                              {emp.profile_photo && <AvatarImage src={emp.profile_photo} />}
                              <AvatarFallback className="bg-slate-100 dark:bg-slate-700 text-slate-600 text-xs">{getInitials(emp.name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-semibold text-slate-900 dark:text-white">{emp.name}</p>
                              <p className="text-xs text-slate-400">{formatCurrency(emp.pay_rate)} / {emp.pay_type === 'hourly' ? 'hr' : 'month'}</p>
                            </div>
                          </div>
                          <select
                            value={emp.salary_template_id || ''}
                            onChange={e => handleAssignTemplate(emp.id, e.target.value)}
                            className="text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 min-w-[180px]"
                            data-testid={`template-select-${emp.id}`}
                          >
                            <option value="">{def ? `Default (${def.name})` : 'No template'}</option>
                            {salaryTemplates.map(t => (
                              <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (Default)' : ''}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}


        {/* PAY RUNS TAB */}
        {activeTab === 'runs' && !selectedRun && (
          <Card className="border-slate-200/60 dark:border-slate-700 overflow-visible" data-testid="payroll-runs">
            <CardContent className="p-0 overflow-visible">
              {payRuns.length > 0 ? (
                <div className="relative w-full overflow-visible">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 dark:border-slate-700">
                      <TableHead className="text-xs font-semibold uppercase tracking-wider">Period</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Employees</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Status</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Total</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider w-32"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payRuns.map(run => (
                      <TableRow key={run.id} className="border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50"
                        onClick={() => handleViewRun(run.id)} data-testid={`payrun-row-${run.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <div>
                              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                {new Date(run.period_start).toLocaleDateString()} — {new Date(run.period_end).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-slate-400">Created {new Date(run.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{run.employee_count}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${
                            run.status === 'processed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                            run.status === 'approved' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            run.status === 'processing' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                          }`}>
                            {run.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm font-bold text-slate-900 dark:text-white">{formatCurrency(run.total_amount, run.currency)}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 justify-end" onClick={e => e.stopPropagation()}>
                            {run.status === 'draft' && (
                              <Button size="sm" variant="outline" onClick={() => handleUpdateRunStatus(run.id, 'approved')}
                                className="text-xs h-7 px-2 gap-1">
                                <Check className="w-3 h-3" /> Approve
                              </Button>
                            )}
                            {run.status === 'approved' && (
                              <Button size="sm" variant="outline" onClick={() => handleUpdateRunStatus(run.id, 'processed')}
                                className="text-xs h-7 px-2 gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Processed
                              </Button>
                            )}
                            {run.status === 'processed' && (
                              <Button size="sm" onClick={() => handleSendPayslips(run.id)}
                                disabled={sendingPayslips}
                                className="text-xs h-7 px-2 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`send-payslips-list-${run.id}`}>
                                {sendingPayslips ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Send
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleViewRun(run.id)} className="text-xs h-7 px-2">
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 mx-auto text-slate-200 dark:text-slate-600 mb-3" />
                  <p className="text-sm text-slate-500 font-medium">No pay runs generated yet</p>
                  <p className="text-xs text-slate-400 mt-1">Click "Generate Pay Run" to create your first payroll</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* PAY RUN DETAIL */}
        {(activeTab === 'run-detail' || (activeTab === 'runs' && selectedRun)) && selectedRun && (
          <div className="space-y-4" data-testid="payrun-detail">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => { setSelectedRun(null); setActiveTab('runs'); }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                <ArrowRight className="w-3 h-3 rotate-180" /> Back to Pay Runs
              </button>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                  Pay Run: {new Date(selectedRun.period_start).toLocaleDateString()} — {new Date(selectedRun.period_end).toLocaleDateString()}
                </h2>
                <p className="text-sm text-slate-400 mt-0.5">
                  {selectedRun.payslips?.length} payslips · Net Total: {formatCurrency(selectedRun.total_amount, selectedRun.currency)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => handleRecalculateRun(selectedRun.id)}
                  className="text-xs text-slate-500 hover:text-blue-600 gap-1" data-testid="recalculate-btn" title="Recalculate using current salary templates">
                  <ArrowRight className="w-3 h-3 animate-spin-once" /> Recalculate
                </Button>
                <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase ${
                  selectedRun.status === 'processed' ? 'bg-emerald-100 text-emerald-700' :
                  selectedRun.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-600'
                }`}>{selectedRun.status}</span>
                {selectedRun.status === 'draft' && (
                  <Button size="sm" onClick={() => handleUpdateRunStatus(selectedRun.id, 'approved')} className="bg-blue-600 hover:bg-blue-700 text-white gap-1">
                    <Check className="w-3.5 h-3.5" /> Approve
                  </Button>
                )}
                {selectedRun.status === 'approved' && (
                  <>
                    <div className="relative">
                      <Button size="sm" onClick={() => setShowExportMenu(showExportMenu === selectedRun.id ? null : selectedRun.id)}
                        className="bg-blue-600 hover:bg-blue-700 text-white gap-1" data-testid="detail-export-btn">
                        <Download className="w-3.5 h-3.5" /> Export
                      </Button>
                      {showExportMenu === selectedRun.id && renderGroupedExportMenu(selectedRun.id)}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleUpdateRunStatus(selectedRun.id, 'processed')} className="gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Mark Processed
                    </Button>
                  </>
                )}
                {selectedRun.status === 'processed' && (
                  <>
                    <Button size="sm" onClick={() => handleSendPayslips(selectedRun.id)}
                      disabled={sendingPayslips}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                      data-testid="send-payslips-btn">
                      {sendingPayslips ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      {sendingPayslips ? 'Sending...' : 'Send Payslips'}
                    </Button>
                    {selectedRun.payslips_sent_at && (
                      <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-1">
                        <Mail className="w-3 h-3" /> Sent {new Date(selectedRun.payslips_sent_at).toLocaleDateString()}
                      </span>
                    )}
                    <div className="relative">
                      <Button size="sm" variant="outline" onClick={() => setShowExportMenu(showExportMenu === selectedRun.id ? null : selectedRun.id)} className="gap-1">
                        <Download className="w-3.5 h-3.5" /> Re-export
                      </Button>
                      {showExportMenu === selectedRun.id && renderGroupedExportMenu(selectedRun.id)}
                    </div>
                  </>
                )}
              </div>
            </div>

            <Card className="border-slate-200/60 dark:border-slate-700">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 dark:border-slate-700">
                      <TableHead className="text-xs font-semibold uppercase tracking-wider">Employee</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider">Type</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-center">Hours Tracked</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Base Pay</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Overtime</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Adjustments</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Net Pay</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedRun.payslips?.map((ps, idx) => {
                      const typeStyle = getTypeStyle(ps.employment_type);
                      const isExpanded = expandedPayslip === ps.id;
                      const hasComponents = ps.components && ps.components.length > 0;
                      return (
                        <React.Fragment key={ps.id || idx}>
                          <TableRow className={`border-slate-200 dark:border-slate-700 ${hasComponents ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50' : ''}`}
                            onClick={() => hasComponents && setExpandedPayslip(isExpanded ? null : ps.id)}
                            data-testid={`payslip-row-${idx}`}>
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                {hasComponents && (
                                  <ChevronRight className={`w-3 h-3 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                )}
                                <div>
                                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{ps.employee_name}</p>
                                  <p className="text-xs text-slate-400 capitalize">{ps.pay_type} · {formatCurrency(ps.pay_rate, ps.currency)}/{ps.pay_type === 'hourly' ? 'hr' : 'period'}
                                    {ps.lop_days > 0 && <span className="text-orange-500 ml-1">· {ps.lop_days} LOP</span>}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${typeStyle.color}`}>
                                {typeStyle.label}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{ps.tracked_hours}h</span>
                              {ps.overtime_hours > 0 && (
                                <span className="text-xs text-amber-500 ml-1">(+{ps.overtime_hours}h OT)</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium text-slate-700 dark:text-slate-300">
                              {formatCurrency(ps.base_pay, ps.currency)}
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium">
                              {ps.overtime_pay > 0 ? (
                                <span className="text-amber-600">{formatCurrency(ps.overtime_pay, ps.currency)}</span>
                              ) : <span className="text-slate-300">—</span>}
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium">
                              {ps.adjustments !== 0 ? (
                                <span className={ps.adjustments > 0 ? 'text-emerald-600' : 'text-red-600'}>
                                  {ps.adjustments > 0 ? '+' : ''}{formatCurrency(ps.adjustments, ps.currency)}
                                </span>
                              ) : <span className="text-slate-300">—</span>}
                            </TableCell>
                            <TableCell className="text-right">
                              <div>
                                <span className="text-sm font-bold text-slate-900 dark:text-white">
                                  {formatCurrency(ps.net_pay || (ps.total_pay + (ps.adjustments || 0)), ps.currency)}
                                </span>
                                {ps.total_deductions > 0 && (
                                  <p className="text-[10px] text-red-400">−{formatCurrency(ps.total_deductions, ps.currency)} ded.</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {(selectedRun.status === 'draft' || selectedRun.status === 'processing') && (
                                  <button onClick={(e) => { e.stopPropagation(); handleOpenEditPay(ps); }}
                                    className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-blue-600 transition-colors"
                                    title="Edit Pay" data-testid={`edit-pay-${idx}`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button onClick={(e) => { e.stopPropagation(); handleDownloadPayslipPDF(selectedRun.id, ps.user_id); }}
                                  className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-blue-600 transition-colors"
                                  title="Download Payslip PDF" data-testid={`download-payslip-${idx}`}>
                                  <Printer className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                          {isExpanded && hasComponents && (
                            <TableRow className="bg-slate-50/50 dark:bg-slate-800/30">
                              <TableCell colSpan={8} className="py-3 px-6">
                                <div className="grid grid-cols-2 gap-6 max-w-lg" data-testid={`breakdown-${idx}`}>
                                  <div>
                                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-1.5">Earnings</p>
                                    {ps.components.filter(c => c.category === 'earning').map((c, ci) => (
                                      <div key={ci} className="flex justify-between text-xs py-0.5">
                                        <span className="text-slate-500">{c.name}</span>
                                        <span className="font-mono font-semibold text-slate-700 dark:text-slate-300">{formatCurrency(c.amount, ps.currency)}</span>
                                      </div>
                                    ))}
                                    <div className="border-t border-slate-200 dark:border-slate-600 mt-1 pt-1 flex justify-between text-xs font-bold">
                                      <span>Gross</span>
                                      <span className="font-mono">{formatCurrency(ps.gross_pay || ps.total_pay, ps.currency)}</span>
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-red-500 uppercase tracking-wider mb-1.5">Deductions</p>
                                    {ps.components.filter(c => c.category === 'deduction').map((c, ci) => (
                                      <div key={ci} className="flex justify-between text-xs py-0.5">
                                        <span className="text-slate-500">{c.name}</span>
                                        <span className="font-mono font-semibold text-red-500">−{formatCurrency(c.amount, ps.currency)}</span>
                                      </div>
                                    ))}
                                    {ps.components.filter(c => c.category === 'deduction').length === 0 && (
                                      <p className="text-xs text-slate-400 italic">No deductions</p>
                                    )}
                                    {ps.total_deductions > 0 && (
                                      <div className="border-t border-slate-200 dark:border-slate-600 mt-1 pt-1 flex justify-between text-xs font-bold">
                                        <span>Total Deductions</span>
                                        <span className="font-mono text-red-600">−{formatCurrency(ps.total_deductions, ps.currency)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {ps.salary_template_name && (
                                  <p className="text-[10px] text-slate-400 mt-2">Template: {ps.salary_template_name}</p>
                                )}
                                {ps.lop_days > 0 && (
                                  <p className="text-[10px] text-orange-500 mt-1">LOP Days: {ps.lop_days} · Paid Days: {ps.paid_days}/30</p>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}
        {/* SETTINGS TAB */}
        {activeTab === 'settings' && payrollSettings && (
          <div className="space-y-6" data-testid="payroll-settings">
            {/* Company Bank Account Section */}
            <Card className="border-slate-200/60 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-indigo-600" />
                  Company Bank Account
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">Debit account used for salary disbursement. This appears as DEBIT_ACC_NO in bank payment exports (ICICI, HDFC).</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Bank Name</label>
                    <input
                      data-testid="company-bank-name"
                      type="text"
                      placeholder="e.g. ICICI Bank"
                      value={payrollSettings.company_bank_name || ''}
                      onChange={e => setPayrollSettings(prev => ({ ...prev, company_bank_name: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Account Number</label>
                    <div className="relative">
                      <input
                        data-testid="company-bank-account"
                        type={payrollSettings._showAccountFull ? 'text' : 'password'}
                        placeholder="e.g. 012345678901234"
                        value={payrollSettings.company_bank_account || ''}
                        onChange={e => setPayrollSettings(prev => ({ ...prev, company_bank_account: e.target.value }))}
                        className="w-full px-3 py-2 pr-16 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm font-mono"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        {payrollSettings.company_bank_account && !payrollSettings._showAccountFull && (
                          <span className="text-[10px] text-slate-400 font-mono">
                            ····{(payrollSettings.company_bank_account || '').slice(-4)}
                          </span>
                        )}
                        <button type="button" onClick={() => setPayrollSettings(prev => ({ ...prev, _showAccountFull: !prev._showAccountFull }))}
                          className="text-slate-400 hover:text-slate-600 p-0.5" data-testid="toggle-account-visibility">
                          {payrollSettings._showAccountFull ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">IFSC Code</label>
                    <input
                      data-testid="company-bank-ifsc"
                      type="text"
                      placeholder="e.g. ICIC0001234"
                      value={payrollSettings.company_bank_ifsc || ''}
                      onChange={e => setPayrollSettings(prev => ({ ...prev, company_bank_ifsc: e.target.value.toUpperCase() }))}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm font-mono"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Professional Tax Section */}
            <Card className="border-slate-200/60 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-blue-600" />
                  Professional Tax (PT)
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">Select your company's operating state to auto-calculate PT based on state-specific slabs.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-w-sm">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">State</label>
                  <select
                    data-testid="pt-state-select"
                    value={payrollSettings.payroll_state || ''}
                    onChange={e => setPayrollSettings(prev => ({ ...prev, payroll_state: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">-- No State Selected (use template default) --</option>
                    {statesList.map(s => (
                      <option key={s.code} value={s.code}>
                        {s.name}{s.has_pt ? '' : ' (No PT)'}
                      </option>
                    ))}
                  </select>
                </div>
                {payrollSettings.payroll_state && (() => {
                  const selected = statesList.find(s => s.code === payrollSettings.payroll_state);
                  return selected ? (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 max-w-sm">
                      <p className="text-xs font-medium text-blue-700 dark:text-blue-400">
                        {selected.has_pt
                          ? `PT will be auto-calculated based on ${selected.name} slabs when generating pay runs.`
                          : `${selected.name} does not levy Professional Tax. PT deduction will be ₹0.`}
                      </p>
                    </div>
                  ) : null;
                })()}
              </CardContent>
            </Card>

            {/* Provident Fund Section */}
            <Card className="border-slate-200/60 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-green-600" />
                  Provident Fund (PF)
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">Enable or disable PF deductions for your organization. When disabled, PF components in salary templates will be skipped.</p>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-center justify-between max-w-sm">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Enable PF Deductions</span>
                  <button
                    data-testid="pf-toggle"
                    onClick={() => setPayrollSettings(prev => ({ ...prev, pf_enabled: !prev.pf_enabled }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      payrollSettings.pf_enabled ? 'bg-green-600' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      payrollSettings.pf_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {payrollSettings.pf_enabled && (
                  <div className="space-y-4 pl-0 border-l-2 border-green-300 dark:border-green-700 ml-1 pl-4">
                    <div className="grid grid-cols-2 gap-4 max-w-sm">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Employee Rate (%)</label>
                        <input
                          data-testid="pf-employee-rate"
                          type="number"
                          min="0"
                          max="100"
                          value={payrollSettings.pf_employee_rate ?? 12}
                          onChange={e => setPayrollSettings(prev => ({ ...prev, pf_employee_rate: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Employer Rate (%)</label>
                        <input
                          data-testid="pf-employer-rate"
                          type="number"
                          min="0"
                          max="100"
                          value={payrollSettings.pf_employer_rate ?? 12}
                          onChange={e => setPayrollSettings(prev => ({ ...prev, pf_employer_rate: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between max-w-sm">
                      <div>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Cap PF at Basic</span>
                        <p className="text-xs text-slate-500">Limit PF calculation to a maximum basic salary</p>
                      </div>
                      <button
                        data-testid="pf-cap-toggle"
                        onClick={() => setPayrollSettings(prev => ({ ...prev, pf_cap_enabled: !prev.pf_cap_enabled }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          payrollSettings.pf_cap_enabled ? 'bg-green-600' : 'bg-slate-300 dark:bg-slate-600'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          payrollSettings.pf_cap_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {payrollSettings.pf_cap_enabled && (
                      <div className="max-w-[200px]">
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Cap Amount (₹)</label>
                        <input
                          data-testid="pf-cap-amount"
                          type="number"
                          min="0"
                          value={payrollSettings.pf_cap_amount ?? 15000}
                          onChange={e => setPayrollSettings(prev => ({ ...prev, pf_cap_amount: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                        />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ESI Section */}
            <Card className="border-slate-200/60 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <IndianRupee className="w-4 h-4 text-orange-600" />
                  Employee State Insurance (ESI)
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">ESI applies to employees with gross salary below the threshold.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between max-w-sm">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Enable ESI</span>
                  <button
                    data-testid="esi-toggle"
                    onClick={() => setPayrollSettings(prev => ({ ...prev, esi_enabled: !prev.esi_enabled }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      payrollSettings.esi_enabled ? 'bg-orange-600' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      payrollSettings.esi_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {payrollSettings.esi_enabled && (
                  <div className="max-w-[200px]">
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Gross Salary Threshold (₹)</label>
                    <input
                      data-testid="esi-threshold"
                      type="number"
                      min="0"
                      value={payrollSettings.esi_threshold ?? 21000}
                      onChange={e => setPayrollSettings(prev => ({ ...prev, esi_threshold: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* TDS / Income Tax Section */}
            <Card className="border-slate-200 dark:border-slate-700" data-testid="tds-settings-card">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">TDS (Income Tax)</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Auto-calculate TDS for salaried employees based on FY 2025-26 New Tax Regime slabs</p>
                  </div>
                  <button
                    data-testid="tds-toggle-btn"
                    type="button"
                    onClick={() => setPayrollSettings(prev => ({ ...prev, tds_enabled: !(prev.tds_enabled !== false) }))}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                      payrollSettings.tds_enabled !== false ? 'bg-green-600' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      payrollSettings.tds_enabled !== false ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                {payrollSettings.tds_enabled !== false && (
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
                    <p className="text-xs text-slate-600 dark:text-slate-400 font-medium mb-2">Tax Slabs (New Regime FY 2025-26)</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-500">
                      <span>Up to ₹4L</span><span className="text-right">Nil</span>
                      <span>₹4L - ₹8L</span><span className="text-right">5%</span>
                      <span>₹8L - ₹12L</span><span className="text-right">10%</span>
                      <span>₹12L - ₹16L</span><span className="text-right">15%</span>
                      <span>₹16L - ₹20L</span><span className="text-right">20%</span>
                      <span>₹20L - ₹24L</span><span className="text-right">25%</span>
                      <span>Above ₹24L</span><span className="text-right">30%</span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2">Standard Deduction: ₹75,000 | Rebate u/s 87A if taxable ≤ ₹12L | 4% Cess</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                data-testid="save-payroll-settings-btn"
                onClick={() => handleSavePayrollSettings({
                  payroll_state: payrollSettings.payroll_state,
                  pf_enabled: payrollSettings.pf_enabled,
                  pf_employee_rate: payrollSettings.pf_employee_rate,
                  pf_employer_rate: payrollSettings.pf_employer_rate,
                  pf_cap_enabled: payrollSettings.pf_cap_enabled,
                  pf_cap_amount: payrollSettings.pf_cap_amount,
                  esi_enabled: payrollSettings.esi_enabled,
                  esi_threshold: payrollSettings.esi_threshold,
                  tds_enabled: payrollSettings.tds_enabled,
                  company_bank_name: payrollSettings.company_bank_name,
                  company_bank_account: payrollSettings.company_bank_account,
                  company_bank_ifsc: payrollSettings.company_bank_ifsc,
                })}
                disabled={savingSettings}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              >
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Pay Modal */}
      <Dialog open={!!editPaySlip} onOpenChange={(open) => { if (!open) setEditPaySlip(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="edit-pay-modal">
          <DialogHeader>
            <DialogTitle className="text-base">Edit Pay — {editPaySlip?.employee_name}</DialogTitle>
            <DialogDescription className="text-xs">
              {editPaySlip?.pay_type} · {formatCurrency(editPaySlip?.pay_rate, editPaySlip?.currency)}/{editPaySlip?.pay_type === 'hourly' ? 'hr' : 'period'}
            </DialogDescription>
          </DialogHeader>

          {editPaySlip && (
            <div className="space-y-4 mt-2">
              {/* Current auto-calculated breakdown */}
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Auto-Calculated</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
                  {editPaySlip.components?.filter(c => !c.is_adjustment).map((c, i) => (
                    <div key={i} className="flex justify-between col-span-2">
                      <span className={c.category === 'earning' ? 'text-emerald-600' : 'text-red-500'}>{c.name}</span>
                      <span className="font-mono font-medium text-slate-700 dark:text-slate-300">
                        {c.category === 'deduction' ? '−' : '+'}{formatCurrency(c.amount, editPaySlip.currency)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* TDS Override */}
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">TDS Override</p>
                  <label className="flex items-center gap-2 text-xs text-slate-500">
                    <input type="checkbox" checked={editPayTdsOverride !== null} onChange={(e) => {
                      if (e.target.checked) {
                        const autoTds = editPaySlip.components?.find(c => c.code === 'tds')?.amount || 0;
                        setEditPayTdsOverride(autoTds);
                      } else {
                        setEditPayTdsOverride(null);
                      }
                    }} className="rounded" data-testid="tds-override-check" />
                    Override auto TDS
                  </label>
                </div>
                {editPayTdsOverride !== null && (
                  <Input
                    type="number"
                    value={editPayTdsOverride}
                    onChange={(e) => setEditPayTdsOverride(Number(e.target.value))}
                    placeholder="Enter TDS amount"
                    className="text-sm"
                    data-testid="tds-override-input"
                  />
                )}
              </div>

              {/* Adjustment Items */}
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">Adjustments</p>
                  <Button variant="outline" size="sm" className="h-6 text-xs gap-1"
                    onClick={() => setEditPayItems(prev => [...prev, { name: '', category: 'earning', amount: '' }])}
                    data-testid="add-adjustment-btn">
                    <Plus className="w-3 h-3" /> Add
                  </Button>
                </div>

                {editPayItems.length === 0 && (
                  <p className="text-xs text-slate-400 italic">No adjustments. Click "Add" to add a bonus, allowance, or deduction.</p>
                )}

                <div className="space-y-2">
                  {editPayItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`adjustment-row-${i}`}>
                      <Input
                        value={item.name}
                        onChange={(e) => setEditPayItems(prev => prev.map((it, idx) => idx === i ? { ...it, name: e.target.value } : it))}
                        placeholder="e.g. Performance Bonus"
                        className="flex-1 h-8 text-xs"
                        data-testid={`adj-name-${i}`}
                      />
                      <select
                        value={item.category}
                        onChange={(e) => setEditPayItems(prev => prev.map((it, idx) => idx === i ? { ...it, category: e.target.value } : it))}
                        className="h-8 text-xs border border-slate-200 dark:border-slate-700 rounded-md px-2 bg-white dark:bg-slate-800"
                        data-testid={`adj-category-${i}`}
                      >
                        <option value="earning">Earning (+)</option>
                        <option value="deduction">Deduction (−)</option>
                      </select>
                      <Input
                        type="number"
                        value={item.amount}
                        onChange={(e) => setEditPayItems(prev => prev.map((it, idx) => idx === i ? { ...it, amount: e.target.value } : it))}
                        placeholder="Amount"
                        className="w-24 h-8 text-xs"
                        data-testid={`adj-amount-${i}`}
                      />
                      <button onClick={() => setEditPayItems(prev => prev.filter((_, idx) => idx !== i))}
                        className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500"
                        data-testid={`adj-remove-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary Preview */}
              {editPayItems.filter(i => i.name && i.amount).length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                  <p className="text-[10px] font-bold text-blue-500 uppercase tracking-wider mb-1">Adjustment Summary</p>
                  {editPayItems.filter(i => i.name && i.amount).map((item, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className={item.category === 'earning' ? 'text-emerald-600' : 'text-red-500'}>{item.name}</span>
                      <span className="font-mono font-medium">{item.category === 'earning' ? '+' : '−'}₹{Math.abs(item.amount).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditPaySlip(null)} className="text-xs h-8" data-testid="edit-pay-cancel">Cancel</Button>
                <Button onClick={handleSaveEditPay} disabled={editPaySaving} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 gap-1" data-testid="edit-pay-save">
                  {editPaySaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
